/**
 * Robot sur quadrillage — logique pure (Boîte à outils, 6e → 4e).
 *
 * Un robot sur un quadrillage de 8 × 8 cases, tourné vers une direction. Les
 * blocs : avancer de n, tourner à gauche / à droite (déplacements RELATIFS),
 * aller vers le haut / le bas / la gauche / la droite de n (ABSOLUS), répéter
 * n fois. L'exécution donne une image par pas (une case ou un quart de tour),
 * le tracé du crayon, et s'arrête au bord ou contre un mur.
 */

export const SIZE = 8;

/** 0 : droite, 1 : haut, 2 : gauche, 3 : bas (sens trigonométrique) */
export type Dir = 0 | 1 | 2 | 3;
export const DX = [1, 0, -1, 0] as const;
export const DY = [0, 1, 0, -1] as const;
export const DIR_NAME = ['la droite', 'le haut', 'la gauche', 'le bas'] as const;

export interface RState {
  x: number;
  y: number;
  dir: Dir;
}

export type Instr =
  | { k: 'fwd'; n: number }
  | { k: 'left' }
  | { k: 'right' }
  | { k: 'move'; dir: Dir; n: number }
  | { k: 'repeat'; n: number; body: Instr[] };

/** chemin d'une instruction dans le programme : indices, puis « b » pour un corps */
export type Path = number[];

export interface Frame {
  state: RState;
  /** instruction en cours (chemin) */
  path: Path;
  /** tour de boucle en cours, pour l'affichage (« 2e tour sur 4 ») */
  lap?: { path: Path; i: number; n: number };
  /** le pas a échoué ici (bord, mur) : le robot reste sur place */
  fail?: boolean;
}

export interface Run {
  frames: Frame[];
  /** segments tracés (clés « x1,y1|x2,y2 » triées) */
  segments: string[];
  error: string | null;
}

export const segKey = (a: { x: number; y: number }, b: { x: number; y: number }) => {
  const s1 = `${a.x},${a.y}`;
  const s2 = `${b.x},${b.y}`;
  return s1 < s2 ? `${s1}|${s2}` : `${s2}|${s1}`;
};

const MAX_FRAMES = 400;

export function run(
  program: readonly Instr[],
  start: RState,
  walls: ReadonlySet<string> = new Set(),
): Run {
  const frames: Frame[] = [{ state: start, path: [] }];
  const segs = new Set<string>();
  let st = start;
  let error: string | null = null;
  const laps: Array<{ path: Path; i: number; n: number }> = [];

  const push = (path: Path) => {
    frames.push({ state: st, path, lap: laps.length ? { ...laps[laps.length - 1]! } : undefined });
    if (frames.length > MAX_FRAMES) throw new Error('long');
  };

  const step = (dir: Dir, path: Path): boolean => {
    const nx = st.x + DX[dir];
    const ny = st.y + DY[dir];
    const blocked =
      nx < 0 || ny < 0 || nx >= SIZE || ny >= SIZE
        ? 'Le robot sortirait du quadrillage : il s’arrête.'
        : walls.has(`${nx},${ny}`)
          ? 'Un mur bloque le robot : il s’arrête.'
          : null;
    if (blocked) {
      error = blocked;
      // une image de plus, sur place, avec le bloc fautif
      frames.push({
        state: st,
        path,
        lap: laps.length ? { ...laps[laps.length - 1]! } : undefined,
        fail: true,
      });
      return false;
    }
    segs.add(segKey(st, { x: nx, y: ny }));
    st = { ...st, x: nx, y: ny };
    push(path);
    return true;
  };

  const exec = (list: readonly Instr[], base: Path): boolean => {
    for (let i = 0; i < list.length; i++) {
      const ins = list[i]!;
      const path = [...base, i];
      switch (ins.k) {
        case 'fwd':
          for (let t = 0; t < ins.n; t++) if (!step(st.dir, path)) return false;
          break;
        case 'move':
          for (let t = 0; t < ins.n; t++) if (!step(ins.dir, path)) return false;
          break;
        case 'left':
          st = { ...st, dir: ((st.dir + 1) % 4) as Dir };
          push(path);
          break;
        case 'right':
          st = { ...st, dir: ((st.dir + 3) % 4) as Dir };
          push(path);
          break;
        case 'repeat':
          for (let t = 0; t < ins.n; t++) {
            laps.push({ path, i: t + 1, n: ins.n });
            const ok = exec(ins.body, path);
            laps.pop();
            if (!ok) return false;
          }
          break;
      }
    }
    return true;
  };

  try {
    exec(program, []);
  } catch {
    error = 'Programme trop long : le robot s’arrête après 400 pas.';
  }
  return { frames, segments: [...segs].sort(), error };
}

/** combien de blocs (pour un défi « avec au plus k blocs ») */
export const blockCount = (p: readonly Instr[]): number =>
  p.reduce((s, i) => s + 1 + (i.k === 'repeat' ? blockCount(i.body) : 0), 0);

/** une vraie boucle : répéter au moins 2 fois un corps qui fait avancer le robot */
export const usesRepeat = (p: readonly Instr[]): boolean =>
  p.some(
    (i) =>
      i.k === 'repeat' &&
      ((i.n >= 2 && i.body.some((b) => b.k === 'fwd' || b.k === 'move' || b.k === 'repeat')) ||
        usesRepeat(i.body)),
  );

/* ------------------------------------------------------------------ */
/* édition du programme (immuable)                                     */
/* ------------------------------------------------------------------ */

/** la liste visée par un chemin de répétition ([] = le programme ; chemin périmé → le programme) */
function listAt(p: Instr[], target: Path): Instr[] {
  let list = p;
  for (const i of target) {
    const ins = list[i];
    if (!ins || ins.k !== 'repeat') return p;
    list = ins.body;
  }
  return list;
}

/** la cible d'ajout après la suppression du bloc `removed` */
export function targetAfterRemove(target: Path, removed: Path): Path {
  const d = removed.length - 1;
  // le bloc supprimé contient la cible (ou l'est)
  if (removed.length <= target.length && removed.every((v, i) => v === target[i])) return [];
  // même liste parente, placé avant : la cible recule d'un rang
  if (
    target.length > d &&
    removed.slice(0, d).every((v, i) => v === target[i]) &&
    removed[d]! < target[d]!
  )
    return target.map((v, i) => (i === d ? v - 1 : v));
  return target;
}

/** la cible d'ajout après l'échange du bloc `moved` avec son voisin */
export function targetAfterMove(target: Path, moved: Path, delta: -1 | 1): Path {
  const d = moved.length - 1;
  if (target.length <= d || !moved.slice(0, d).every((v, i) => v === target[i])) return target;
  const k = moved[d]!;
  if (target[d] === k) return target.map((v, i) => (i === d ? k + delta : v));
  if (target[d] === k + delta) return target.map((v, i) => (i === d ? k : v));
  return target;
}

const clone = (p: readonly Instr[]): Instr[] =>
  p.map((i) => (i.k === 'repeat' ? { ...i, body: clone(i.body) } : { ...i }));

export function addAt(p: readonly Instr[], target: Path, ins: Instr): Instr[] {
  const q = clone(p);
  listAt(q, target).push(ins);
  return q;
}

export function removeAt(p: readonly Instr[], path: Path): Instr[] {
  const q = clone(p);
  listAt(q, path.slice(0, -1)).splice(path[path.length - 1]!, 1);
  return q;
}

export function updateAt(p: readonly Instr[], path: Path, f: (i: Instr) => Instr): Instr[] {
  const q = clone(p);
  const list = listAt(q, path.slice(0, -1));
  const k = path[path.length - 1]!;
  list[k] = f(list[k]!);
  return q;
}

export function moveAt(p: readonly Instr[], path: Path, delta: -1 | 1): Instr[] {
  const q = clone(p);
  const list = listAt(q, path.slice(0, -1));
  const k = path[path.length - 1]!;
  const j = k + delta;
  if (j < 0 || j >= list.length) return q;
  [list[k], list[j]] = [list[j]!, list[k]!];
  return q;
}

/** texte d'un bloc : « avancer de 3 », « répéter 4 fois » */
export function instrText(i: Instr): string {
  switch (i.k) {
    case 'fwd':
      return `avancer de ${i.n}`;
    case 'left':
      return 'tourner à gauche';
    case 'right':
      return 'tourner à droite';
    case 'move':
      return `aller vers ${DIR_NAME[i.dir]} de ${i.n}`;
    case 'repeat':
      return `répéter ${i.n} fois`;
  }
}

/* ------------------------------------------------------------------ */
/* défis                                                               */
/* ------------------------------------------------------------------ */

export interface Challenge {
  id: string;
  title: string;
  /** consigne */
  text: string;
  start: RState;
  /** case à atteindre */
  goal?: { x: number; y: number };
  /** segments à tracer exactement */
  draw?: string[];
  walls?: string[];
  /** il faut une boucle */
  needRepeat?: boolean;
  /** au plus ce nombre de blocs */
  maxBlocks?: number;
}

const square = (x: number, y: number, c: number) => {
  const out: string[] = [];
  for (let i = 0; i < c; i++) {
    out.push(segKey({ x: x + i, y }, { x: x + i + 1, y }));
    out.push(segKey({ x: x + c, y: y + i }, { x: x + c, y: y + i + 1 }));
    out.push(segKey({ x: x + i, y: y + c }, { x: x + i + 1, y: y + c }));
    out.push(segKey({ x, y: y + i }, { x, y: y + i + 1 }));
  }
  return out.sort();
};

const stairs = (x: number, y: number, k: number) => {
  const out: string[] = [];
  let cx = x;
  let cy = y;
  for (let i = 0; i < k; i++) {
    out.push(segKey({ x: cx, y: cy }, { x: cx, y: cy + 1 }));
    cy++;
    out.push(segKey({ x: cx, y: cy }, { x: cx + 1, y: cy }));
    cx++;
  }
  return out.sort();
};

export const CHALLENGES: readonly Challenge[] = [
  {
    id: 'star',
    title: 'Va sur l’étoile',
    text: 'Amène le robot sur l’étoile.',
    start: { x: 0, y: 0, dir: 0 },
    goal: { x: 4, y: 3 },
  },
  {
    id: 'wall',
    title: 'Contourne le mur',
    text: 'Atteins l’étoile sans traverser le mur.',
    start: { x: 0, y: 3, dir: 0 },
    goal: { x: 6, y: 3 },
    walls: ['3,1', '3,2', '3,3', '3,4', '3,5'],
  },
  {
    id: 'square',
    title: 'Un carré',
    text: 'Trace le carré de côté 3 en pointillés.',
    start: { x: 2, y: 2, dir: 0 },
    draw: square(2, 2, 3),
  },
  {
    id: 'square-loop',
    title: 'Carré avec une boucle',
    text: 'Trace le carré de côté 4 avec au plus 3 blocs (une boucle « répéter »).',
    start: { x: 1, y: 1, dir: 0 },
    draw: square(1, 1, 4),
    needRepeat: true,
    maxBlocks: 3,
  },
  {
    id: 'stairs',
    title: 'Escalier',
    text: 'Trace l’escalier de 4 marches avec une boucle.',
    start: { x: 1, y: 1, dir: 1 },
    draw: stairs(1, 1, 4),
    needRepeat: true,
  },
];

export function judge(c: Challenge, program: readonly Instr[]): { ok: boolean; say: string } {
  const r = run(program, c.start, new Set(c.walls ?? []));
  const end = r.frames[r.frames.length - 1]!.state;
  if (r.error) return { ok: false, say: r.error };
  if (c.goal && (end.x !== c.goal.x || end.y !== c.goal.y))
    return { ok: false, say: 'Le robot ne finit pas sur l’étoile.' };
  if (c.draw) {
    const want = new Set(c.draw);
    const missing = c.draw.filter((s) => !r.segments.includes(s)).length;
    const extra = r.segments.filter((s) => !want.has(s)).length;
    if (missing || extra)
      return {
        ok: false,
        say:
          missing && extra
            ? `Il manque ${missing} trait${missing > 1 ? 's' : ''} et il y en a ${extra} de trop.`
            : missing
              ? `Il manque ${missing} trait${missing > 1 ? 's' : ''}.`
              : `Il y a ${extra} trait${extra > 1 ? 's' : ''} de trop.`,
      };
  }
  if (c.needRepeat && !usesRepeat(program))
    return { ok: false, say: 'Le dessin est bon : refais-le avec une boucle « répéter ».' };
  if (c.maxBlocks && blockCount(program) > c.maxBlocks)
    return {
      ok: false,
      say: `C’est juste, mais avec ${blockCount(program)} blocs : fais-le en ${c.maxBlocks} au plus.`,
    };
  return { ok: true, say: 'Défi réussi !' };
}

/* ------------------------------------------------------------------ */
/* prévoir le résultat (5e)                                            */
/* ------------------------------------------------------------------ */

export const PREDICT: ReadonlyArray<{ start: RState; program: Instr[] }> = [
  {
    start: { x: 1, y: 1, dir: 0 },
    program: [{ k: 'fwd', n: 3 }, { k: 'left' }, { k: 'fwd', n: 2 }],
  },
  {
    start: { x: 0, y: 0, dir: 1 },
    program: [
      {
        k: 'repeat',
        n: 3,
        body: [{ k: 'fwd', n: 2 }, { k: 'right' }, { k: 'fwd', n: 1 }, { k: 'left' }],
      },
    ],
  },
  {
    start: { x: 6, y: 6, dir: 2 },
    program: [
      { k: 'repeat', n: 4, body: [{ k: 'fwd', n: 2 }, { k: 'left' }] },
      { k: 'fwd', n: 3 },
    ],
  },
  {
    start: { x: 2, y: 5, dir: 3 },
    program: [
      { k: 'fwd', n: 2 },
      { k: 'repeat', n: 2, body: [{ k: 'left' }, { k: 'fwd', n: 2 }] },
    ],
  },
  {
    start: { x: 0, y: 7, dir: 0 },
    program: [
      {
        k: 'repeat',
        n: 3,
        body: [{ k: 'fwd', n: 2 }, { k: 'right' }, { k: 'fwd', n: 2 }, { k: 'left' }],
      },
    ],
  },
];
